import { memo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Constants
import {
    SegmentTypesLabels,
    CompositeSegmentOperation,
    l,
    SegmentObjectType,
    SegmentTypeIconClasses,
} from 'constants/common';
import { Icon } from '@geneui/components';
// Import Components
import UsedInSection from 'components/common/UsedInSection';
import SegmentClientsCount from 'components/common/SegmentClientsCount';
// Import Enums
import { SegmentClientsCountRequestEnum } from 'enums';

const SegmentGeneralInfo = ({
    segmentId,
    type,
    name,
    description,
    isPredefined,
    usedInCampaigns,
    compositeConfiguration,
    targetObject,
    predefinedKeyValue,
}) => {
    const { t } = useTranslation();

    return (
        <>
            <div className="crm-object-modal-head">
                <div className="crm-object-modal-info-inner">
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.SegmentId)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{segmentId}</span>
                    </div>
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.Title)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">{name}</span>
                    </div>

                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.Description)}</span>
                        <span className="ellipsis-text kv-value kv-value-css">
                            {isEmpty(description) ? t(l.NA) : description}
                        </span>
                    </div>
                    {!isNil(compositeConfiguration) && (
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Operation)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {t(CompositeSegmentOperation[compositeConfiguration.Operation])}
                            </span>
                        </div>
                    )}
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">{t(l.Type)}</span>
                        <div className="ellipsis-text kv-value kv-value-css">
                            <div className="crm-object-info-item">
                                <Icon type={SegmentTypeIconClasses[type]} />
                                <p>{t(SegmentTypesLabels[type])}</p>
                            </div>
                        </div>
                    </div>
                    {!isEmpty(predefinedKeyValue) && (
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Regions)}</span>
                            <div className="ellipsis-text kv-value kv-value-css">{predefinedKeyValue.regions}</div>
                        </div>
                    )}
                    {!isEmpty(predefinedKeyValue) && (
                        <div className="key-value-h">
                            <span className="ellipsis-text kv-label">{t(l.Categories)}</span>
                            <div className="ellipsis-text kv-value kv-value-css">{predefinedKeyValue.categories}</div>
                        </div>
                    )}
                    <div className="key-value-h">
                        <span className="ellipsis-text kv-label">
                            {targetObject === SegmentObjectType.Client
                                ? t(l.NumberOfCustomers)
                                : t(l.NumberOfNonCustomers)}
                        </span>
                        <div className="ellipsis-text kv-value kv-value-css crm-segment-filters-header-blocks crm-segment-filters-header-left-side">
                            <SegmentClientsCount
                                isDisabled={isNil(segmentId)}
                                config={{
                                    type: SegmentClientsCountRequestEnum.SegmentId,
                                    segmentId: segmentId,
                                }}
                            />
                        </div>
                    </div>
                    <UsedInSection
                        isLoadingUsedIn={isNil(usedInCampaigns)}
                        usedInData={usedInCampaigns}
                        isVisible={!isPredefined}
                        notUsedInDisplayNameKey={l.SegmentIsNotUsed}
                    />
                </div>
            </div>
        </>
    );
};

SegmentGeneralInfo.propTypes = {
    type: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    usedInCampaigns: PropTypes.array,
    compositeConfiguration: PropTypes.object,
    isPredefined: PropTypes.bool,
    targetObject: PropTypes.number.isRequired,
    segmentId: PropTypes.number.isRequired,
    predefinedKeyValue: PropTypes.object,
};

SegmentGeneralInfo.defaultProps = {
    isPredefined: false,
    usedInCampaigns: null,
    compositeConfiguration: null,
};

export default memo(SegmentGeneralInfo);
